import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [galleryDropdownOpen, setGalleryDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setGalleryDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const scrollToSection = (href: string) => {
    const el = document.querySelector(href);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileOpen(false);
    setGalleryDropdownOpen(false);
  };

  const goToGalleryPage = () => {
    navigate('/gallery');
    setIsMobileOpen(false);
    setGalleryDropdownOpen(false);
  };

  const textColor = isScrolled ? '#2C2C2C' : '#fff';
  const bgClass = isScrolled
    ? 'bg-white/95 backdrop-blur-sm shadow-sm'
    : 'bg-transparent';

  const desktopLinkClass =
    'font-body text-sm transition-colors hover:text-teal whitespace-nowrap cursor-pointer';

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${bgClass}`}>
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          <a
            href="#"
            className="flex items-center"
            onClick={(e) => {
              e.preventDefault();
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          >
            <img
              src="https://static.readdy.ai/image/0e9775581447daa16a16441c9f113381/b806c298fd480fe9b398164c0551d937.png"
              alt="Aria Property Improvement"
              className="h-9 md:h-10 w-auto object-contain"
            />
          </a>

          <div className="hidden md:flex items-center gap-6">
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className={desktopLinkClass}
              style={{ color: textColor }}
            >
              Home
            </a>

            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setGalleryDropdownOpen(!galleryDropdownOpen)}
                className={`${desktopLinkClass} flex items-center gap-1`}
                style={{ color: textColor }}
              >
                Gallery
                <div className="w-3 h-3 flex items-center justify-center">
                  <i className="ri-arrow-down-s-line text-xs"></i>
                </div>
              </button>
              {galleryDropdownOpen && (
                <div className="absolute top-full left-0 mt-2 bg-white rounded-lg shadow-lg border border-concrete py-2 min-w-[180px]">
                  <button
                    onClick={() => {
                      scrollToSection('#gallery');
                    }}
                    className="block w-full text-left px-4 py-2 font-body text-sm text-charcoal hover:bg-teal-50 hover:text-teal transition-colors"
                  >
                    Handyman
                  </button>
                  <button
                    onClick={() => {
                      scrollToSection('#gallery');
                    }}
                    className="block w-full text-left px-4 py-2 font-body text-sm text-charcoal hover:bg-teal-50 hover:text-teal transition-colors"
                  >
                    Bathrooms
                  </button>
                  <button
                    onClick={() => {
                      scrollToSection('#gallery');
                    }}
                    className="block w-full text-left px-4 py-2 font-body text-sm text-charcoal hover:bg-teal-50 hover:text-teal transition-colors"
                  >
                    Kitchens
                  </button>
                  <button
                    onClick={() => {
                      scrollToSection('#gallery');
                    }}
                    className="block w-full text-left px-4 py-2 font-body text-sm text-charcoal hover:bg-teal-50 hover:text-teal transition-colors"
                  >
                    Full Remodels
                  </button>
                </div>
              )}
            </div>

            <a
              href="#about"
              onClick={(e) => {
                e.preventDefault();
                scrollToSection('#about');
              }}
              className={desktopLinkClass}
              style={{ color: textColor }}
            >
              Our Process
            </a>
            <a
              href="#testimonials"
              onClick={(e) => {
                e.preventDefault();
                scrollToSection('#testimonials');
              }}
              className={desktopLinkClass}
              style={{ color: textColor }}
            >
              What Customers Say
            </a>
            <a
              href="#about"
              onClick={(e) => {
                e.preventDefault();
                scrollToSection('#about');
              }}
              className={desktopLinkClass}
              style={{ color: textColor }}
            >
              About Us
            </a>
            <a
              href="#contact"
              onClick={(e) => {
                e.preventDefault();
                scrollToSection('#contact');
              }}
              className={desktopLinkClass}
              style={{ color: textColor }}
            >
              Contact
            </a>
            <a
              href="#contact"
              onClick={(e) => {
                e.preventDefault();
                scrollToSection('#contact');
              }}
              className="bg-teal hover:bg-teal-dark text-white font-body text-sm font-semibold px-5 py-2.5 rounded-md transition-colors whitespace-nowrap cursor-pointer"
            >
              Get a Quote
            </a>
          </div>

          <button
            className="md:hidden w-10 h-10 flex items-center justify-center"
            onClick={() => setIsMobileOpen(!isMobileOpen)}
            aria-label="Toggle menu"
          >
            <div className="w-6 h-6 flex items-center justify-center">
              {isMobileOpen ? (
                <i className="ri-close-line text-2xl" style={{ color: textColor }}></i>
              ) : (
                <i className="ri-menu-line text-2xl" style={{ color: textColor }}></i>
              )}
            </div>
          </button>
        </div>
      </div>

      {isMobileOpen && (
        <div className="md:hidden bg-white border-t border-concrete shadow-lg">
          <div className="px-4 py-4 space-y-2">
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: 'smooth' });
                setIsMobileOpen(false);
              }}
              className="block font-body text-charcoal hover:text-teal text-sm py-2.5"
            >
              Home
            </a>
            <button
              onClick={() => setGalleryDropdownOpen(!galleryDropdownOpen)}
              className="block w-full text-left font-body text-charcoal hover:text-teal text-sm py-2.5 flex items-center justify-between"
            >
              Gallery
              <div className="w-3 h-3 flex items-center justify-center">
                {galleryDropdownOpen ? (
                  <i className="ri-arrow-up-s-line text-xs"></i>
                ) : (
                  <i className="ri-arrow-down-s-line text-xs"></i>
                )}
              </div>
            </button>
            {galleryDropdownOpen && (
              <div className="pl-4 space-y-1">
                <button onClick={() => scrollToSection('#gallery')} className="block w-full text-left font-body text-charcoal/70 text-sm py-1.5">Handyman</button>
                <button onClick={() => scrollToSection('#gallery')} className="block w-full text-left font-body text-charcoal/70 text-sm py-1.5">Bathrooms</button>
                <button onClick={() => scrollToSection('#gallery')} className="block w-full text-left font-body text-charcoal/70 text-sm py-1.5">Kitchens</button>
                <button onClick={() => scrollToSection('#gallery')} className="block w-full text-left font-body text-charcoal/70 text-sm py-1.5">Full Remodels</button>
              </div>
            )}
            <a href="#about" onClick={(e) => { e.preventDefault(); scrollToSection('#about'); }} className="block font-body text-charcoal hover:text-teal text-sm py-2.5">Our Process</a>
            <a href="#testimonials" onClick={(e) => { e.preventDefault(); scrollToSection('#testimonials'); }} className="block font-body text-charcoal hover:text-teal text-sm py-2.5">What Customers Say</a>
            <a href="#about" onClick={(e) => { e.preventDefault(); scrollToSection('#about'); }} className="block font-body text-charcoal hover:text-teal text-sm py-2.5">About Us</a>
            <a href="#contact" onClick={(e) => { e.preventDefault(); scrollToSection('#contact'); }} className="block font-body text-charcoal hover:text-teal text-sm py-2.5">Contact</a>
            <a
              href="#contact"
              onClick={(e) => {
                e.preventDefault();
                scrollToSection('#contact');
              }}
              className="block bg-teal text-white font-body text-sm font-semibold px-5 py-3 rounded-md text-center mt-3"
            >
              Get a Quote
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}